import { Component, computed, DestroyRef, effect, inject, OnInit, signal, untracked } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { NgTemplateOutlet } from "@angular/common";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { SvgIconComponent } from "angular-svg-icon";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { switchMap, tap } from "rxjs";
import { sanitizeDirectusArray } from "../../../../common/utils/sanitize-directus-object";
import { SeoService } from "../../../services/seo.service";
import { AdBlockComponent } from "../../components/ad-block/ad-block.component";
import { HeaderBlockComponent } from "../../components/header-block/header-block.component";
import { PaginatorComponent } from "../category/components/paginator/paginator.component";
import { SearchComponent, SearchOutput } from "../category/components/search/search.component";
import { SupplierCardShimmerComponent } from "../category/components/supplier-card/components/supplier-card-shimmer/supplier-card-shimmer.component";
import { SupplierCardComponent } from "../category/components/supplier-card/supplier-card.component";
import { SearchResultsService } from "./store/search-results.service";
import { SearchResultsStore } from "./store/search-results.store";

@Component({
    selector: "packbo-search-results",
    templateUrl: "./search-results.template.html",
    providers: [SearchResultsService, SearchResultsStore],
    imports: [
        SvgIconComponent,
        TranslocoPipe,
        HeaderBlockComponent,
        AdBlockComponent,
        NgTemplateOutlet,
        SupplierCardComponent,
        SupplierCardShimmerComponent,
        PaginatorComponent,
        SearchComponent
    ]
})
export class SearchResultsComponent implements OnInit {
    private readonly searchResultsService = inject(SearchResultsService);
    private readonly searchResultsStore = inject(SearchResultsStore);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly seoService = inject(SeoService);
    private readonly transloco = inject(TranslocoService);

    public readonly adBlock = this.searchResultsStore.adBlock;
    public readonly headerBlock = this.searchResultsStore.headerBlock;
    public readonly suppliers = this.searchResultsStore.suppliers;
    public readonly supplierImagesLoading = this.searchResultsStore.supplierImagesLoading;
    public readonly loading = this.searchResultsStore.loading;
    public readonly itemsCertifications = this.searchResultsStore.itemsCertifications;
    public readonly pagination = this.searchResultsStore.pagination;

    protected readonly searchValue = signal<string | null>(null);
    protected readonly postalCode = signal<string | null>(null);
    protected readonly distance = signal<{ value: number; label: string } | null>(null);
    protected readonly isEcoFriendly = signal<boolean>(false);
    protected readonly selectedCertificationsIds = signal<number[]>([]);
    protected readonly currentPage = signal(this.pagination().currentPage);

    protected readonly shouldShowShimmer = computed(() => {
        return this.loading() || this.supplierImagesLoading();
    });
    protected readonly shouldShowPagination = computed(() => {
        return this.pagination().totalPages > 1;
    });

    constructor() {
        effect(() => {
            const currentPage = this.currentPage();
            const isEcoFriendly = this.isEcoFriendly();

            if (currentPage > 0) {
                untracked(() => {
                    this.searchResultsStore.setIsEcoFriendly(isEcoFriendly);
                    this.searchResultsStore.setSupplierImagesLoading(true);
                    this.searchResultsStore.setSuppliers([]);

                    if (isEcoFriendly) {
                        this.currentPage.set(1);
                    }

                    this.searchResultsService
                        .searchSuppliers({
                            searchTerm: this.searchValue() ?? "",
                            postalCode: this.postalCode() ?? "",
                            distance: this.distance()?.value?.toString() ?? "",
                            certifications: this.selectedCertificationsIds() ?? [],
                            isEcoFriendly: isEcoFriendly,
                            page: currentPage
                        })
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe();
                });
            }
        });
    }

    public ngOnInit(): void {
        this.searchResultsService
            .fetchHeaderBlock()
            .pipe(
                tap(() => this.updateSeoTags()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
        this.searchResultsService.fetchAds().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.searchResultsService.fetchCertifications().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

        this.route.queryParams
            .pipe(
                switchMap((params) => {
                    const searchTerm = params["search"];
                    const postalCode = params["postalCode"];
                    const distance = params["distance"];
                    const certifications = params["certifications"] ?? [];

                    this.searchValue.set(searchTerm);
                    this.postalCode.set(postalCode);
                    this.distance.set(distance ? { value: Number(distance), label: `${distance}km` } : null);
                    this.selectedCertificationsIds.set(certifications);

                    return this.searchResultsService.searchSuppliers({
                        searchTerm: searchTerm ?? "",
                        postalCode: postalCode ?? "",
                        distance: distance ?? "",
                        certifications: certifications ?? [],
                        isEcoFriendly: this.isEcoFriendly(),
                        page: this.currentPage()
                    });
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.transloco.langChanges$
            .pipe(
                tap(() => this.updateSeoTags()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    public getSuppliers(searchOutput: SearchOutput): void {
        const { search, postalCode, distance, certifications } = searchOutput;

        this.searchResultsService
            .searchSuppliers({
                searchTerm: search ?? "",
                postalCode: postalCode ?? "",
                distance: distance ?? "",
                certifications: certifications ?? [],
                isEcoFriendly: this.isEcoFriendly(),
                page: 1
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.router.navigate([], {
                    queryParams: {
                        search: search,
                        postalCode: postalCode,
                        distance: distance
                    }
                });
            });
    }

    public onSupplierImagesLoaded(): void {
        this.searchResultsStore.setSupplierImagesLoading(false);
    }

    private updateSeoTags(): void {
        const headerBlock = this.headerBlock();
        if (headerBlock) {
            const translations = sanitizeDirectusArray(headerBlock.translations_refs).map((t) => ({
                languages_code: t.languages_code,
                title: t.title,
                meta_description: t.subtitle,
                slug: null
            }));

            this.seoService.updateMetaTags(
                {
                    translations,
                    noIndex: false,
                    noFollow: false,
                    isPrivate: false
                },
                this.router.url
            );
        }
    }
}
